/**
 * Teen Subliminals Page
 * Background audio sessions for study, focus, confidence, and sleep.
 * Uses TeenThemeContext for customizable look and feel.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BookOpen,
  Brain,
  Star,
  Moon,
  Sun,
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Headphones,
  Clock
} from "lucide-react";
import axios from "axios";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  "book-open": BookOpen,
  brain: Brain,
  star: Star,
  moon: Moon,
  sun: Sun
};

const TeenSubliminalsContent = () => {
  const navigate = useNavigate();
  const { theme, currentPalette } = useTeenTheme();
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetDetails, setPresetDetails] = useState(null);
  const [duration, setDuration] = useState(30);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [showHeadphonesHint, setShowHeadphonesHint] = useState(false);
  
  // Theme colors
  const colors = {
    base: theme?.colors?.base || '#0f0f23',
    primary: currentPalette?.primary || theme?.colors?.primary || '#818CF8',
    accent: currentPalette?.accent || theme?.colors?.accent || '#F472B6',
    textPrimary: theme?.colors?.textPrimary || '#ffffff',
    textSecondary: theme?.colors?.textSecondary || 'rgba(255,255,255,0.6)',
  };
  
  const timerRef = useRef(null);
  const affirmationIndexRef = useRef(0);
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadPresets();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const loadPresets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/subliminals`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPresets(res.data.presets);
    } catch (err) {
      console.error("Error loading presets:", err);
    }
  };

  const handleSelectPreset = async (preset) => {
    setSelectedPreset(preset);
    setDuration(preset.default_duration);
    setShowHeadphonesHint(preset.brainwave !== "none");
    
    try {
      const res = await axios.get(`${API_URL}/api/teen/subliminals/${preset.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPresetDetails(res.data);
    } catch (err) {
      console.error("Error loading preset details:", err);
    }
  };

  const handleStart = () => {
    setIsPlaying(true);
    setElapsed(0);
    affirmationIndexRef.current = 0;
    
    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        if (prev >= duration * 60) {
          handleStop();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const handleStop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsPlaying(false);
    setElapsed(0);
  };

  const handlePause = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsPlaying(false);
  };

  const handleResume = () => {
    setIsPlaying(true);
    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        if (prev >= duration * 60) {
          handleStop();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (elapsed / (duration * 60)) * 100;

  // Preset selection grid
  if (!selectedPreset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
        {/* Header */}
        <div className="p-4">
          <button
            onClick={() => navigate("/teen")}
            className="text-white/60 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 pt-2 pb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Study & Focus</h1>
          <p className="text-white/60">Background audio for concentration</p>
        </div>

        {/* Presets */}
        <div className="px-6 pb-8 space-y-4">
          {presets.map((preset, index) => {
            const Icon = ICONS[preset.icon] || BookOpen;
            return (
              <motion.button
                key={preset.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelectPreset(preset)}
                className={`w-full p-5 rounded-2xl bg-gradient-to-r ${preset.color} shadow-xl flex items-center gap-4 text-left`}
                data-testid={`subliminal-preset-${preset.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-0.5">{preset.name}</h3>
                  <p className="text-white/80 text-sm">{preset.description}</p>
                </div>
                <div className="text-white/60 text-sm">
                  {preset.default_duration}m
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Info */}
        <div className="px-6 pb-8">
          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-start gap-3">
              <Headphones size={20} className="text-blue-400 mt-0.5" />
              <div>
                <p className="text-blue-300 text-sm font-medium">Best with headphones</p>
                <p className="text-white/50 text-xs mt-1">
                  Subliminal affirmations work best when you can hear them clearly
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Session player
  const Icon = ICONS[selectedPreset.icon] || BookOpen;

  // Not started yet - show config
  if (!isPlaying && elapsed === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23] flex flex-col">
        {/* Header */}
        <div className="p-4">
          <button
            onClick={() => setSelectedPreset(null)}
            className="text-white/60 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${selectedPreset.color} flex items-center justify-center shadow-xl mb-6`}
          >
            <Icon size={40} className="text-white" />
          </motion.div>

          <h1 className="text-2xl font-bold text-white mb-2">{selectedPreset.name}</h1>
          <p className="text-white/60 text-center mb-8">{selectedPreset.description}</p>

          {/* Headphones hint */}
          {showHeadphonesHint && (
            <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 mb-6 flex items-center gap-3">
              <Headphones size={20} className="text-purple-400" />
              <span className="text-purple-300 text-sm">Use headphones for binaural beats</span>
            </div>
          )}

          {/* Duration selector */}
          <div className="w-full max-w-xs mb-8">
            <label className="text-white/60 text-sm mb-3 block text-center">Duration</label>
            <div className="flex justify-center gap-3">
              {selectedPreset.duration_options.map(d => (
                <button
                  key={d}
                  onClick={() => setDuration(d)}
                  className={`px-5 py-3 rounded-xl font-semibold transition-all ${
                    duration === d
                      ? 'bg-white text-gray-900'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {d}m
                </button>
              ))}
            </div>
          </div>

          {/* Start button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleStart}
            className={`w-full max-w-xs py-4 rounded-xl bg-gradient-to-r ${selectedPreset.color} text-white font-bold text-lg shadow-xl flex items-center justify-center gap-3`}
            data-testid="start-subliminal-btn"
          >
            <Play size={24} />
            <span>Start Session</span>
          </motion.button>
        </div>
      </div>
    );
  }

  // Active session
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`min-h-screen bg-gradient-to-b ${selectedPreset.color} flex flex-col`}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={handleStop}
          className="text-white/80 hover:text-white flex items-center gap-2"
        >
          <Square size={18} />
          <span className="text-sm">End</span>
        </button>
        
        <span className="text-white/80 text-sm font-medium">{selectedPreset.name}</span>
        
        <div className="w-16" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Large circular progress */}
        <div className="relative w-64 h-64 mb-8">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="128"
              cy="128"
              r="120"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="8"
            />
            <motion.circle
              cx="128"
              cy="128"
              r="120"
              fill="none"
              stroke="white"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={754}
              strokeDashoffset={754 - (754 * progress) / 100}
              transition={{ duration: 0.5 }}
            />
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Icon size={40} className="text-white mb-3" />
            <span className="text-4xl font-bold text-white">
              {formatTime((duration * 60) - elapsed)}
            </span>
            <span className="text-white/60 text-sm mt-1">remaining</span>
          </div>
        </div>

        {/* Current affirmation (showing what's playing subliminal) */}
        {presetDetails?.affirmations && (
          <motion.div
            key={Math.floor(elapsed / 8) % presetDetails.affirmations.length}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-sm"
          >
            <p className="text-white/40 text-sm mb-1">Playing subliminal:</p>
            <p className="text-white/80 text-lg italic">
              "{presetDetails.affirmations[Math.floor(elapsed / 8) % presetDetails.affirmations.length]}"
            </p>
          </motion.div>
        )}
      </div>

      {/* Controls */}
      <div className="p-6 flex items-center justify-center gap-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={isPlaying ? handlePause : handleResume}
          className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center"
          data-testid="pause-resume-btn"
        >
          {isPlaying ? (
            <Pause size={28} className="text-white" />
          ) : (
            <Play size={28} className="text-white ml-1" />
          )}
        </motion.button>
      </div>

      {/* Volume */}
      <div className="px-8 pb-8 flex items-center gap-4">
        <VolumeX size={18} className="text-white/60" />
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="flex-1 h-1 bg-white/20 rounded-full appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
        />
        <Volume2 size={18} className="text-white/60" />
      </div>
    </motion.div>
  );
};

// Main component wraps with theme provider
const TeenSubliminalsPage = () => {
  return (
    <TeenThemeProvider>
      <TeenSubliminalsContent />
    </TeenThemeProvider>
  );
};

export default TeenSubliminalsPage;
